import notify from 'notify-zh';
import DOMPurify from 'dompurify';

export function addErrors(formElement, errors) {
	if (typeof formElement !== 'object') {
		console.error('Selector formElement is not an object.');

		return;
	}

	for (const fieldName in errors) {
		const fields = getFieldsByName(formElement, fieldName);

		if (fields.length > 0) {
			fields.forEach((field) => {
				field.classList.add('is-invalid');

				const nextButton = field.nextElementSibling;
				if (nextButton && nextButton.tagName.toLowerCase() === 'button') {
					nextButton.classList.add('is-invalid');
				}
			});

			const wrapper = fields[0].closest('div.mb-3, div.form-radio, fieldset');

			if (wrapper) {
				let feedback = wrapper.querySelector('.invalid-feedback');

				if (!feedback) {
					feedback = document.createElement('div');
					feedback.className = 'invalid-feedback';
					wrapper.appendChild(feedback);
				}

				feedback.textContent = Array.isArray(errors[fieldName]) ? errors[fieldName][0] : errors[fieldName];
				feedback.style.display = 'block';
			}
		}
	}

	// for (const fieldName in errors) {
	// 	formElement
	// 		.find('[name="' + fieldName + '"]')
	// 		.addClass('is-invalid')
	// 		.closest('div.mb-3, div.form-radio')
	// 		.append('<div class="invalid-feedback">' + errors[fieldName] + '</div>');

	// 	formElement.find('.invalid-feedback').show();

	// 	formElement
	// 		.find('[name="' + fieldName + '"]')
	// 		.next('button')
	// 		.addClass('is-invalid');
	// }
}

export function clearCursorWait() {
	document.body.style.cursor = 'default';

	hide('.loading');
}

export function clearErrors(formElement) {
	// Ensure formElement is a valid object
	if (!(formElement instanceof Element)) {
		console.warn('Selector formElement is not a valid DOM element.');

		return;
	}

	// Remove 'has-error', 'has-success', and 'is-invalid' classes from invalid fields
	formElement.querySelectorAll('.form-control, .form-select, .choices, fieldset, input, textarea, select').forEach((el) => {
		el.classList.remove('has-error', 'has-success', 'is-invalid');
	});

	// Remove all error messages
	formElement.querySelectorAll('.invalid-feedback').forEach((el) => el.remove());
}

function getFieldsByName(formElement, fieldName) {
	const names = [
		fieldName,
		fieldName.replace(/\.(\w+)/g, '[$1]'),
		fieldName.replace(/\[(\w+)\]/g, '.$1')
	];
	const uniqueNames = [...new Set(names)];

	for (const name of uniqueNames) {
		const fields = Array.from(formElement.querySelectorAll(`[name="${name}"]`));

		if (fields.length > 0) {
			return fields;
		}
	}

	return [];
}

export function clearMessage(element) {
	if (element) {
		element.innerHTML = '';
		element.classList.remove('alert', 'alert-danger', 'alert-default', 'alert-info', 'alert-success', 'alert-warning', 'alert-dismissible');
	}
}

export function colorizeStatus(status) {
	if (typeof status !== 'string') {
		return makeLabel('Error', 'danger', 'exclamation');
	}

	const map = {
		complete: ['Complete', 'success', 'check'],
		deleted: ['Deleted', 'danger', 'trash'],
		error: ['Failed', 'danger', 'times'],
		failed: ['Failed', 'danger', 'times'],
		found: ['Found', 'success', 'check'],
		not_found: ['Not Found', 'danger', 'exclamation'],
		processing: ['Processing', 'info', 'circle-o-notch fa-spin'],
		queued: ['Queued', 'secondary', 'repeat'],
		ready: ['Ready', 'success', 'check'],
		requested: ['Requested', 'secondary', 'plus'],
		rerun: ['Rerun', 'secondary', 'repeat'],
		running: ['Running', 'info', 'circle-o-notch fa-spin'],
		stopped: ['Stopped', 'warning', 'exclamation'],
		updated: ['Updated', 'warning', 'exclamation']
	};

	const key = status.toLowerCase();
	const [label, badge, icon] = map[key] || [status, 'info', 'question'];

	// sanitize the label text
	const safeLabel = DOMPurify.sanitize(label);

	return makeLabel(safeLabel, badge, icon);
}

export function configureNotify() {
	// Configure global settings
	notify.config({
		defaultTime: 4000,
		position: 'top-right'
		// backgrounds: {
		// 	success: '#A3CFBB',
		// 	error: '#EF4444'
		// }
	});
}

export function formatNumber(value) {
	return isNumeric(value) ? parseFloat(value).toLocaleString('en-US') : '-';
}

export function formatDate(value) {
	const date = parseUtc(value);
	if (!date) return '';

	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: '2-digit'
	});
}

export function formatDateTime(value) {
	const date = parseUtc(value);
	if (!date) return '';

	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: true
	});
}

export function formatTime(value, withSeconds = false) {
	const date = parseUtc(value);
	if (!date) return '';

	return date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		second: withSeconds ? '2-digit' : undefined,
		hour12: true
	});
}

export function highlight(text, search) {
	if (!text) return '';

	// sanitize input first
	const safeText = DOMPurify.sanitize(text);

	if (!search) return safeText;

	// escape regex chars in search
	const safeSearch = escapeRegExp(search);

	// highlight
	const html = safeText.replace(new RegExp(`(${safeSearch})`, 'gi'), '<mark class="highlight">$1</mark>');

	// sanitize output again
	return DOMPurify.sanitize(html);
}

export function isNumeric(n) {
	return !isNaN(parseFloat(n)) && isFinite(n);
}

export function removeItem(key) {
	localStorage.removeItem(key);
}

export function secondsToHms(seconds, short_version) {
	let display = '';

	if (seconds <= 1) {
		display = '< 1s';
	} else if (seconds > 1 && seconds <= 2) {
		display = '~ 1s';
	} else {
		const days = Math.floor(seconds / (24 * 60 * 60));
		seconds -= days * (24 * 60 * 60);
		const hours = Math.floor(seconds / (60 * 60));
		seconds -= hours * (60 * 60);
		const minutes = Math.floor(seconds / 60);
		seconds -= minutes * 60;
		seconds = Math.round(seconds);

		let displayDays, displayHours, displayMinutes, displaySeconds;

		if (typeof short_version !== 'undefined' && short_version) {
			displayDays = days > 0 ? days + 'd ' : '';
			displayHours = hours > 0 ? hours + 'h ' : '';
			displayMinutes = minutes.toString().paddingLeft('00') + 'm ';
			displaySeconds = seconds.toString().paddingLeft('00') + 's ';
		} else {
			displayDays = days > 0 ? days + ' day, ' : '';
			displayHours = hours > 0 ? hours + ' hour' + (hours !== 1 ? 's' : '') + ' ' : '';
			displayMinutes = minutes + ' minute' + (minutes !== 1 ? 's' : '') + ', ';
			displaySeconds = seconds + ' second' + (seconds !== 1 ? 's' : '');
		}

		display = displayDays + displayHours + displayMinutes + displaySeconds;
	}

	return display;
}

export function setCheckAll(container, parentSelector, childSelector, callback) {
	const parent = container.querySelector(parentSelector);
	const children = Array.from(container.querySelectorAll(`${childSelector}:not(:disabled)`));

	if (!parent) return;

	// Initialize state
	parent.checked = false;
	children.forEach((child) => (child.checked = false));

	// Handle parent checkbox change
	parent.removeEventListener('change', parent._checkAllHandler);
	parent._checkAllHandler = function(e) {
		const checked = e.currentTarget.checked;
		children.forEach((child) => {
			if (!child.disabled) child.checked = checked;
		});
		if (typeof callback === 'function') callback();
	};
	parent.addEventListener('change', parent._checkAllHandler);

	// Handle individual child checkbox changes
	children.forEach((child) => {
		child.removeEventListener('change', child._individualHandler);
		child._individualHandler = function() {
			const allChecked = children.every((c) => c.checked);
			parent.checked = allChecked;
			if (typeof callback === 'function') callback();
		};
		child.addEventListener('change', child._individualHandler);
	});
}

export function setCursorWait() {
	document.body.style.cursor = 'wait';

	show('.loading');
}

export function setItem(key, value) {
	console.log(arguments.callee.name, key, value);

	localStorage.setItem(key, value);
}

export function sortByValue(obj) {
	const sortedArray = [];

	// push each json object entry in array by [key, value]
	for (let i in obj) {
		sortedArray.push([obj[i], i]);
	}

	// run native sort function and returns sorted array
	return sortedArray.sort();
}

export function stripTicks(val) {
	return val.replace(/`/g, '');
}

export function stripSort(val) {
	var regex = new RegExp(' (DE|A)SC$', 'i');

	return val.replace(regex, '');
}

export function updateBrowserUrl(params, origin = window.location.origin, pathname = window.location.pathname) {
	const urlParamsForBrowser = { ...params };
	delete urlParamsForBrowser.show;

	const newUrl = `${origin}${pathname}?${new URLSearchParams(urlParamsForBrowser)}`;
	window.history.replaceState(null, '', newUrl);
}

export function writeMessage(element, message, status = 'danger') {
	if (!element) return;

	clearMessage(element);

	const bsMap = {
		success: 'success',
		warning: 'warning',
		no_data: 'warning',
		danger: 'danger',
		error: 'danger'
	};

	const type = bsMap[status] || 'danger';
	const safeMessage = message.replace(/`(.+?)`/g, '<strong>$1</strong>');

	element.innerHTML = `
		<div class="alert alert-${type} alert-dismissible fade show" role="alert">
			${safeMessage}
			<button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
		</div>
	`;
}

export function writeNotify(message, status = 'info') {
	const finalMessage = stripTicks(message || '');

	const map = {
		no_data: 'warning',
		warning: 'warning',
		success: 'success',
		info: 'info',
		danger: 'error',
		error: 'error'
	};

	const type = map[status] || 'error';
	notify[type]({ message: finalMessage });
}

function hide(selector) {
	return showHide(selector, false);
}

function makeLabel(display, type, icon) {
	let html = `<span class="badge badge-status badge-${type}">`;

	if (icon) {
		html += `<i class="fa fa-${icon}"></i> `;
	}

	const safeText = DOMPurify.sanitize(display);

	html += safeText + '</span>';

	return html;
}

function parseUtc(value) {
	if (!value) return null;
	return new Date(value.endsWith('Z') ? value : `${value}Z`);
}

function escapeRegExp(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function show(selector) {
	return showHide(selector, true);
}

function showHide(selector, isShow) {
	document.querySelectorAll(selector).forEach((el) => {
		el.style.display = isShow ? 'block' : 'none';
	});

	return true;
}

export function drawReportCounts(report_counts) {
	if (typeof report_counts?.opg?.complete === 'number') {
		const el = document.getElementById('opg_count');
		if (el) el.textContent = report_counts.opg.complete;
	}

	if (typeof report_counts?.uio?.complete === 'number') {
		const el = document.getElementById('uio_count');
		if (el) el.textContent = report_counts.uio.complete;
	}
}
